import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { EMPTY_GUID, isGuid } from 'src/app/_helpers/guid.helper';
import { CustomObjectTypeEnum } from 'src/app/enums/customObjectType.enum';
import {
    workforceRouteConfig,
    WorkforceTabLink,
} from 'src/app/shared/constants/workforceRouteConfig.constants';
import { CustomObjectService } from './customObject.service';

/**
 * Builds the Workforce tab bar shared by Shifts, Job Dictionary and Job Mapping.
 *
 * The Workforce employee-table tab cannot live in the static
 * `workforceRouteConfig` because its route is dynamic
 * (`/dashboard/nodes/{employeeId}`, where `employeeId` is resolved at runtime
 * from the EmployeeEndPoint custom object). Each page previously had to resolve
 * and prepend it itself; duplicating that logic caused DASH-2723 (two pages
 * shipped the naive static config and dropped the Workforce tab). This service
 * centralises the resolve + prepend + graceful-degrade behaviour so all three
 * pages stay in sync.
 */
@Injectable({ providedIn: 'root' })
export class WorkforceTabsService {
    constructor(private customObjectService: CustomObjectService) {}

    /**
     * Resolves the Workforce tab bar links: the dynamic Workforce employee-table
     * tab prepended to the three static tabs (Shifts | Job Dictionary | Job
     * Mapping). If the EmployeeEndPoint lookup is empty or fails, only the static
     * tabs are returned so the tab bar never blanks out.
     *
     * The lookup is resolved fresh per subscription rather than memoised: a
     * failed lookup is swallowed upstream (`CustomObjectService.getTypeLookup`
     * catches and returns `of([])`), so caching would sticky-pin the degraded,
     * Workforce-tab-less result for the whole session — the exact regression this
     * ticket fixes. Re-fetching lets a later navigation self-heal.
     */
    getWorkforceTabs(): Observable<WorkforceTabLink[]> {
        return this.customObjectService
            .getTypeLookup(CustomObjectTypeEnum.EmployeeEndPoint)
            .pipe(
                map((result) => {
                    // The EmployeeEndPoint node id arrives as a UUID string (CustomObjectLookup.id). Only a
                    // real node id yields a usable /dashboard/nodes/{id} route; the empty guid, '' and any
                    // non-id value (path-traversal safety) fall back to the static tabs.
                    const employeeId = result?.[0]?.id;
                    return isGuid(employeeId) && employeeId !== EMPTY_GUID
                        ? [
                              { routerLink: `/dashboard/nodes/${employeeId}`, title: 'Workforce' },
                              ...workforceRouteConfig,
                          ]
                        : [...workforceRouteConfig];
                }),
                catchError(() => of([...workforceRouteConfig])),
            );
    }
}
